import { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../../../../utils/i18n';
import {
  Box,
  Typography,
  Stack,
  Button,
  Grid,
  CircularProgress,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Alert,
  TablePagination
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';

import PickerFieldTable from '../../../../components/PickerFieldTable';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { useCompanyDataList, getCompanyById } from '../../../../stores/companyStore';
import { useTireBatchListAll } from '../../../../stores/tireBatchStore';
import { fleetApi } from '../../../../api/axiosClient';
import { HtmlTableContainer, HtmlTable, StyledTableCell, StyledTableRow } from '../historical/TireHistoricalPage';
import TirePerformanceHistoryModal from './TirePerformanceHistoryModal';
import { buildTirePerformanceRows, getPerformanceColumns, formatPerformanceCell, getPerformanceExportValue } from './tirePerformanceUtils';

const getFormattedDate = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

// Same login-company resolution used across tire pages: localStorage companyId -> user.company
const getLoginCompanyId = () => {
  try {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem('companyId');
    if (stored) return String(stored);
    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      const u = JSON.parse(rawUser);
      const id = u?.company ?? u?.company_id;
      if (id != null && id !== '') return String(id);
    }
  } catch (e) {
    console.error('Failed to resolve login company', e);
  }
  return null;
};

export default function TirePerformancePage() {
  const { t } = useLanguage();
  const performanceColumns = useMemo(() => getPerformanceColumns(t), [t]);
  const TABLE_COL_SPAN = performanceColumns.length + 1;
  const [selectedCompany, setSelectedCompany] = useState(() => getLoginCompanyId());
  // Keeps the selected company item so its name is displayed even if it is not on the current picker page
  const [selectedCompanyItem, setSelectedCompanyItem] = useState(null);
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
  const [selectedSerialNumberItems, setSelectedSerialNumberItems] = useState([]);
  const [failedSerialNumbers, setFailedSerialNumbers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedData = useMemo(() => {
    if (!data) return [];
    const start = page * rowsPerPage;
    return data.slice(start, start + rowsPerPage);
  }, [data, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportOpen = Boolean(exportAnchorEl);
  const [isExporting, setIsExporting] = useState(false);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name',
    formatOption: (item) => ({ ...item, value: item.id, label: item.name })
  });

  // Resolve login company name for default selection display
  useEffect(() => {
    const loginCompanyId = getLoginCompanyId();
    if (!loginCompanyId) return;
    let active = true;
    getCompanyById(loginCompanyId)
      .then((res) => {
        const comp = res?.data && !Array.isArray(res.data) ? res.data : res;
        if (active && comp && (comp.id != null || comp.name)) {
          setSelectedCompanyItem({ ...comp, id: comp.id ?? loginCompanyId, value: comp.id ?? loginCompanyId, label: comp.name });
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const serialNumberPickerParams = usePickerPagination({
    fetchHook: useTireBatchListAll,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      selectedCompany ? [{ value: selectedCompany, label: selectedCompany }] : [],
      state.searchKey,
      state.searchValue
    ],
    searchKey: 'serial_number',
    formatOption: (item) => ({
      ...item,
      value: item.serial_number,
      label: item.serial_number
    })
  });

  const handleSerialNumberChange = (val) => {
    const ids = Array.isArray(val) ? val.map(String) : [];
    const pool = [...(serialNumberPickerParams.options || []), ...selectedSerialNumberItems];
    const items = ids.map(
      (id) => pool.find((o) => String(o.serial_number ?? o.value) === id) || { value: id, label: id, serial_number: id }
    );
    setSelectedSerialNumbers(ids);
    setSelectedSerialNumberItems(items);
  };

  const getSerialNumberLabel = (id) => {
    const item = selectedSerialNumberItems.find((o) => String(o.serial_number ?? o.value) === String(id));
    return item?.serial_number || item?.label || String(id);
  };

  const handleSearch = async () => {
    if (!selectedCompany || selectedSerialNumbers.length === 0 || !selectedDate) return;
    setIsLoading(true);
    setHasSearched(true);
    setFailedSerialNumbers([]);
    try {
      const formattedDate = selectedDate.format('YYYY-MM-DD');
      const results = await Promise.allSettled(
        selectedSerialNumbers.map((sn) =>
          fleetApi.get(`/tire-performance?serialNumber=${sn}&date=${formattedDate}&companyId=${selectedCompany}`)
        )
      );

      const payloads = [];
      const failed = [];
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled') {
          const payload = result.value?.data?.data ?? result.value?.data ?? null;
          if (payload) {
            const snItem = selectedSerialNumberItems[idx];
            payload.serial_number = getSerialNumberLabel(selectedSerialNumbers[idx]);
            payload.last_position = snItem?.last_position || '-';
            payload.last_vehicle = snItem?.last_vehicle || '-';
            payloads.push(payload);
          }
        } else {
          console.error(result.reason);
          failed.push(getSerialNumberLabel(selectedSerialNumbers[idx]));
        }
      });

      setFailedSerialNumbers(failed);
      setData(buildTirePerformanceRows(payloads));
      setPage(0);
    } catch (err) {
      console.error(err);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const exportToCSV = () => {
    setIsExporting(true);
    try {
      const csvData = Papa.unparse({
        fields: performanceColumns.map((col) => col.headerName),
        data: data.map((row) => performanceColumns.map((col) => getPerformanceExportValue(row, col)))
      });
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `tire-performance-${getFormattedDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Performance');

      worksheet.columns = performanceColumns.map((col) => ({
        header: col.headerName,
        key: col.field,
        width: 20
      }));

      data.forEach((row) => {
        const tireValues = {};
        performanceColumns.forEach((col) => {
          tireValues[col.field] = getPerformanceExportValue(row, col);
        });
        worksheet.addRow(tireValues);
      });

      const headerRow = worksheet.getRow(1);
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF5166AE' }
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `tire-performance-${getFormattedDate()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF('landscape');
      doc.setFontSize(14);
      doc.text(`Tire Performance Report`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${dayjs().format('YYYY-MM-DD HH:mm')}`, 14, 22);

      const tableData = data.map((row) => performanceColumns.map((col) => formatPerformanceCell(row, col)));

      autoTable(doc, {
        head: [performanceColumns.map((col) => col.headerName)],
        body: tableData,
        startY: 30,
        theme: 'grid',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [25, 118, 210] }
      });

      doc.save(`tire-performance-${getFormattedDate()}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 88px)' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            {t('tirePerformance.title', 'Tire Performance')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              'tirePerformance.description',
              'Calculate and analyze CPK (Cost Per Kilometer) and CPH (Cost Per Hour) for your tires based on specific target dates.'
            )}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<DownloadIcon />}
            onClick={handleExportClick}
            disabled={isExporting || !hasSearched || !data || data.length === 0}
            sx={{ bgcolor: '#ffffff', whiteSpace: 'nowrap' }}
          >
            {isExporting ? t('tireCardHistory.exporting', 'Exporting...') : t('tireCardHistory.export', 'Export')}
          </Button>
          <Menu anchorEl={exportAnchorEl} open={exportOpen} onClose={handleExportClose}>
            <MenuItem onClick={exportToCSV}>{t('tireCardHistory.exportCsv', 'Export to CSV')}</MenuItem>
            <MenuItem onClick={exportToExcel}>{t('tireCardHistory.exportExcel', 'Export to Excel')}</MenuItem>
            <MenuItem onClick={exportToPDF}>{t('tireCardHistory.exportPdf', 'Export to PDF')}</MenuItem>
          </Menu>
        </Box>
      </Stack>

      <Card elevation={0} sx={{ mb: 1, border: '1px solid #e2e8f0' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item size={{ xs: 12, md: 3 }}>
              <PickerFieldTable
                label={t('tireHistorical.company', 'Company')}
                id="company"
                name="company"
                value={selectedCompany}
                onChange={(val) => {
                  setSelectedCompany(val);
                  setSelectedCompanyItem((companyPickerParams.options || []).find((o) => String(o.id ?? o.value) === String(val)) || null);
                  setSelectedSerialNumbers([]);
                  setSelectedSerialNumberItems([]);
                }}
                columns={[{ field: 'name', headerName: t('companyName', 'Company Name') }]}
                {...companyPickerParams}
                selectedItems={selectedCompanyItem ? [selectedCompanyItem] : []}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 3 }}>
              <PickerFieldTable
                label={t('tyreItem.serialNumber', 'Serial Number')}
                id="serial_number"
                name="serial_number"
                multiSelect
                value={selectedSerialNumbers}
                onChange={handleSerialNumberChange}
                disabled={!selectedCompany}
                columns={[
                  { field: 'serial_number', headerName: t('tyreItem.serialNumberExport', 'Serial Number') },
                  {
                    field: 'tire_brand_name',
                    headerName: t('tireRepairScrap.brandExport', 'Brand'),
                    renderCell: (r) => r.join?.tire_item_tire_brand_name || '-'
                  },
                  {
                    field: 'tire_size_code',
                    headerName: t('tireRepairScrap.sizeExport', 'Size'),
                    renderCell: (r) => r.join?.tire_item_tire_size_code_internal || r.join?.tire_item_tire_size_code || '-'
                  },
                  {
                    field: 'pattern',
                    headerName: t('specification.pattern', 'Pattern'),
                    renderCell: (r) => r.join?.m_tire_spec_pattern || '-'
                  },
                  {
                    field: 'air_pressure',
                    headerName: t('tireCardHistory.airPressurePsi', 'Air Pressure (psi)'),
                    renderCell: (r) => {
                      if (r.last_inspection?.air_pressure) return `${r.last_inspection.air_pressure} (last inspection)`;
                      if (r.air_pressure) return `${r.air_pressure} (base)`;
                      if (r.join?.m_tire_spec_pressure_std) return `${r.join.m_tire_spec_pressure_std} (standard)`;
                      return '-';
                    }
                  },
                  {
                    field: 'tread_depth',
                    headerName: t('tireCardHistory.treadDepthMm', 'Tread Depth'),
                    renderCell: (r) => {
                      if (r.last_inspection?.tread_depth) return `${r.last_inspection.tread_depth} (last inspection)`;
                      if (r.tread_depth) return `${r.tread_depth} (base)`;
                      if (r.join?.m_tire_spec_original_tread_depth) return `${r.join.m_tire_spec_original_tread_depth} (standard)`;
                      return '-';
                    }
                  },
                  { field: 'last_position', headerName: t('tireRepairScrap.lastPosition', 'Last Position') },
                  { field: 'last_vehicle', headerName: t('tireHistorical.lastVehicle', 'Last Vehicle') }
                ]}
                getOptionId={(item) => item?.serial_number || item?.value || item}
                {...serialNumberPickerParams}
                selectedItems={selectedSerialNumberItems}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('tirePerformance.targetDate', 'Target Date')}
                  format="DD/MM/YYYY"
                  value={selectedDate}
                  onChange={(newDate) => setSelectedDate(newDate)}
                  disableFuture
                  slotProps={{
                    textField: { fullWidth: true, size: 'small' }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item size={{ xs: 12, md: 3 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={!selectedCompany || selectedSerialNumbers.length === 0 || !selectedDate || isLoading}
                startIcon={<SearchIcon />}
                fullWidth
                sx={{ height: 40 }}
              >
                {isLoading ? t('tirePerformance.generating', 'Generating...') : t('tireRepairScrap.search', 'Search')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!isLoading && failedSerialNumbers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Failed to load tire performance for: {failedSerialNumbers.join(', ')}
        </Alert>
      )}

      <HtmlTableContainer>
        <HtmlTable style={{ minWidth: 2200 }}>
          <thead>
            <tr>
              {performanceColumns.map((col) => {
                let stickyStyle = {};
                if (col.field === 'serial_number') {
                  stickyStyle = {
                    position: 'sticky',
                    left: 0,
                    zIndex: 3,
                    width: 160,
                    minWidth: 160,
                    maxWidth: 160,
                    boxSizing: 'border-box',
                    borderRight: '1px solid #e2e8f0'
                  };
                }
                return (
                  <StyledTableCell key={col.field} style={stickyStyle}>
                    {col.headerName}
                  </StyledTableCell>
                );
              })}
              <StyledTableCell style={{ textAlign: 'center' }}>{t('details', 'Details')}</StyledTableCell>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={TABLE_COL_SPAN} align="center" style={{ padding: '48px 0' }}>
                  <CircularProgress size={32} thickness={5} />
                </td>
              </tr>
            ) : !hasSearched ? (
              <tr>
                <td colSpan={TABLE_COL_SPAN} align="center" style={{ padding: '48px 0' }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('tireHistorical.pleaseSelectFilters', 'Please select filters and click Search.')}
                  </Typography>
                </td>
              </tr>
            ) : !data || data.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COL_SPAN} align="center" style={{ padding: '48px 0' }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('dashboardAsset.noDataFound', 'No data found.')}
                  </Typography>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, idx) => (
                <StyledTableRow key={row.serial_number || idx}>
                  {performanceColumns.map((col) => {
                    let stickyStyle = {};
                    if (col.field === 'serial_number') {
                      stickyStyle = {
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: 'inherit',
                        width: 160,
                        minWidth: 160,
                        maxWidth: 160,
                        boxSizing: 'border-box',
                        fontWeight: 600,
                        borderRight: '1px solid #e2e8f0'
                      };
                    }
                    return (
                      <td key={col.field} style={stickyStyle}>
                        {formatPerformanceCell(row, col)}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setSelectedRow(row);
                        setDetailModalOpen(true);
                      }}
                      sx={{ textTransform: 'none', minWidth: '80px' }}
                    >
                      {t('details', 'Details')}
                    </Button>
                  </td>
                </StyledTableRow>
              ))
            )}
          </tbody>
        </HtmlTable>
      </HtmlTableContainer>

      <Box sx={{ borderTop: 'none', bgcolor: '#ffffff', borderRadius: '0 0 8px 8px', border: '1px solid #e2e8f0', mt: 0 }}>
        <TablePagination
          component="div"
          count={data.length || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>

      {detailModalOpen && (
        <TirePerformanceHistoryModal
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          data={{ ...selectedRow, company_id: selectedRow.company_id || selectedCompany }}
          endDate={selectedDate}
        />
      )}
    </Box>
  );
}
